import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Configuracoes.css';
import api from '../services/api';
import { toast } from 'react-toastify';
import UsuariosTab from '../components/UsuariosTab';
import EmpresaCamposForm from '../components/EmpresaCamposForm';

const TABS = [
  { id: 'empresas', label: 'Empresas', icon: 'domain' },
  { id: 'usuarios', label: 'Usuários', icon: 'group' },
];

function AdminSistema() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('empresas');

  useEffect(() => {
    if (localStorage.getItem('is_superuser') !== 'true') {
      navigate('/dashboard');
    }
  }, [navigate]);

  if (localStorage.getItem('is_superuser') !== 'true') return null;

  return (
    <div className="cfg-page">
      <div className="cfg-page-header">
        <h2 className="cfg-page-title">Admin do Sistema</h2>
        <p className="cfg-page-sub">Gerencie as empresas e usuários de toda a plataforma.</p>
      </div>

      <div className="cfg-layout">
        <aside className="cfg-sidebar">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`cfg-tab-btn${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <span className="material-symbols-outlined">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </aside>

        <div className="cfg-content">
          {tab === 'empresas' && <EmpresasTab />}
          {tab === 'usuarios' && <UsuariosTab />}
        </div>
      </div>
    </div>
  );
}

// ── Empresas (tenants) ──────────────────────────────────────────────────────────

const EMPTY_EMPRESA_FORM = {
  nome: '', nome_fantasia: '', razao_social: '', cnpj: '', ie: '', im: '', telefones: [], ativo: true,
};

function EmpresasTab() {
  const fileInputRef = useRef(null);
  const [empresas, setEmpresas]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_EMPRESA_FORM);
  const [logo, setLogo]           = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving]       = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => { fetchEmpresas(); }, []);

  const fetchEmpresas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/empresas/', { headers: headers() });
      setEmpresas(res.data);
    } catch {
      toast.error('Erro ao carregar empresas.');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_EMPRESA_FORM);
    setLogo(null);
    setLogoPreview(null);
    setShowForm(true);
  };

  const openEdit = (empresa) => {
    setEditing(empresa);
    setForm({
      nome:          empresa.nome,
      nome_fantasia: empresa.nome_fantasia || '',
      razao_social:  empresa.razao_social  || '',
      cnpj:          empresa.cnpj          || '',
      ie:            empresa.ie            || '',
      im:            empresa.im            || '',
      telefones:     empresa.telefones     || [],
      ativo:         empresa.ativo,
    });
    setLogo(null);
    setLogoPreview(empresa.logo || null);
    setShowForm(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'telefones') formData.append(key, JSON.stringify(value));
        else if (key === 'ativo') formData.append(key, value ? 'true' : 'false');
        else formData.append(key, value);
      });
      if (logo) formData.append('logo', logo);

      if (editing) {
        const res = await api.patch(`/empresas/${editing.id}/`, formData, { headers: headers() });
        setEmpresas(empresas.map((emp) => (emp.id === editing.id ? res.data : emp)));
        toast.success('Empresa atualizada.');
      } else {
        const res = await api.post('/empresas/', formData, { headers: headers() });
        setEmpresas([res.data, ...empresas]);
        toast.success('Empresa criada.');
      }
      setShowForm(false);
    } catch (err) {
      const data = err.response?.data;
      const msg = typeof data === 'object'
        ? Object.values(data).flat().join(' ')
        : 'Erro ao salvar empresa.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (str) =>
    str ? new Date(str).toLocaleDateString('pt-BR') : '—';

  return (
    <div className="cfg-card">
      <div className="cfg-card-accent" />
      <div className="cfg-card-body">
        <header className="cfg-section-header cfg-section-header--between">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span className="material-symbols-outlined cfg-section-icon">domain</span>
            <div>
              <h3 className="cfg-section-title">Empresas Cadastradas</h3>
              <p className="cfg-section-sub">Crie, edite ou desative tenants da plataforma.</p>
            </div>
          </div>
          <button className="cfg-btn-primary" onClick={openCreate}>
            <span className="material-symbols-outlined">add_business</span>
            Nova Empresa
          </button>
        </header>

        {showForm && (
          <div className="cfg-user-form">
            <h4 className="cfg-user-form-title">
              {editing ? 'Editar Empresa' : 'Nova Empresa'}
            </h4>
            <form onSubmit={handleSave}>
              <EmpresaCamposForm
                form={form}
                setForm={setForm}
                logoPreview={logoPreview}
                fileInputRef={fileInputRef}
                onFileChange={handleFileChange}
                mostrarAtivo
              />

              <div className="cfg-form-actions">
                <button type="button" className="cfg-btn-secondary" onClick={() => setShowForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="cfg-btn-primary" disabled={saving}>
                  <span className="material-symbols-outlined">{editing ? 'save' : 'add_business'}</span>
                  {saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Empresa'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="cfg-table-wrap">
          <table className="cfg-table">
            <thead>
              <tr>
                <th></th>
                <th>Empresa</th>
                <th className="center">Usuários</th>
                <th className="center">Status</th>
                <th className="center">Criada em</th>
                <th className="right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j}><div className="skeleton" /></td>
                    ))}
                  </tr>
                ))
              ) : empresas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="cfg-empty">Nenhuma empresa cadastrada.</td>
                </tr>
              ) : (
                empresas.map((empresa) => (
                  <tr key={empresa.id}>
                    <td>
                      {empresa.logo
                        ? <img src={empresa.logo} alt="" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4 }} />
                        : <span className="material-symbols-outlined" style={{ color: '#c4c6cc' }}>domain</span>
                      }
                    </td>
                    <td className="td-name">{empresa.nome}</td>
                    <td className="center">{empresa.total_usuarios}</td>
                    <td className="center">
                      {empresa.ativo
                        ? <span className="badge badge-ok">Ativa</span>
                        : <span className="badge badge-error">Inativa</span>
                      }
                    </td>
                    <td className="center">{formatDate(empresa.criado_em)}</td>
                    <td className="right">
                      <div className="td-actions">
                        <button
                          className="prd-icon-btn"
                          onClick={() => openEdit(empresa)}
                          title="Editar"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminSistema;
